import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { EventsAPI } from '../api';
import { UserContext } from '../context/UserContext';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { EVENT_TYPES } from '../utils/eventTypes';
import { EVENT_TYPE_SCHEMAS } from '../utils/eventTypeSchemas';

export default function EventNew() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const isAdmin = Array.isArray(user?.roles) && user.roles.includes('admin');
  const canManageEvents = isAdmin || (Array.isArray(user?.permissions) && user.permissions.includes('events.manage'));
  if (!canManageEvents) return <div style={{ padding:24 }}>Keine Berechtigung: Du kannst keine Events anlegen.</div>;

  const [form, setForm] = useState({
    title: '', description: '', tags: '', posterUrl: '', startAt: '', endAt: '',
    locationType: 'online', venueAddress: '', onlineUrl: '', visibility: 'public',
    registrationMode: 'open',
    capacity: '', waitlistEnabled: true, participantListPublic: true, waitlistPublic: false,
    eventType: '', customEventType: '', typeExtras: {}, userExtras: [
      { label: '', value: '' },
      { label: '', value: '' },
      { label: '', value: '' },
      { label: '', value: '' },
      { label: '', value: '' },
    ], termsText: ''
  });
  const [posterFile, setPosterFile] = useState(null);
  const [owners, setOwners] = useState([]);
  const [ownerId, setOwnerId] = useState(null);

  // Load possible owners (active users) and preselect current user
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/users/list');
        const list = res?.data?.items || [];
        setOwners(list);
        const meId = user?.id;
        if (meId && list.some(u => u.id === meId)) setOwnerId(meId);
        else if (list.length > 0) setOwnerId(list[0].id);
      } catch {}
    })();
  }, [user?.id]);

  const descriptionPreview = useMemo(() => {
    try {
      const raw = marked.parse(form.description || '');
      return { __html: DOMPurify.sanitize(raw) };
    } catch { return { __html: '' }; }
  }, [form.description]);

  async function onSubmit(e) {
    e.preventDefault();
    try {
      const finalEventType = form.eventType === '__custom__'
        ? (form.customEventType?.trim() || null)
        : (form.eventType || null);
      // Nur definierte Keys der Schemafelder übernehmen
      const schema = EVENT_TYPE_SCHEMAS[finalEventType] || [];
      const pickedExtras = {};
      for (const def of schema) {
        const v = form.typeExtras?.[def.key];
        if (v != null && String(v).trim() !== '') pickedExtras[def.key] = v;
      }
      // Bis zu 5 benutzerdefinierte Felder
      const pickedUserExtras = (form.userExtras || [])
        .slice(0, 5)
        .map(row => ({ label: (row.label||'').trim(), value: (row.value||'').trim() }))
        .filter(row => row.label !== '' || row.value !== '');
  // Backend erwartet customFields als Array mit max. 5 Einträgen – hier nur die benutzerdefinierten Felder
      const payload = {
        title: form.title.trim(),
        description: form.description,
        tags: form.tags ? form.tags.split(',').map(s=>s.trim()).filter(Boolean) : [],
        posterUrl: form.posterUrl || null,
        startAt: form.startAt,
        endAt: form.endAt || null,
        locationType: form.locationType,
        venueAddress: form.locationType === 'venue' ? (form.venueAddress || null) : null,
        onlineUrl: form.locationType === 'online' ? (form.onlineUrl || null) : null,
        visibility: form.visibility,
  registrationMode: form.registrationMode,
        capacity: form.capacity ? parseInt(form.capacity, 10) : null,
        waitlistEnabled: !!form.waitlistEnabled,
        participantListPublic: !!form.participantListPublic,
        waitlistPublic: !!form.waitlistPublic,
        eventType: finalEventType,
        typeExtras: pickedExtras,
        customFields: pickedUserExtras,
        ownerId: ownerId ? parseInt(ownerId, 10) : undefined,
        termsText: form.termsText || null,
      };
      const ev = await EventsAPI.create(payload);
      // Optional: Poster-Datei nachreichen, falls gewählt
      if (posterFile) {
        try {
          await EventsAPI.uploadPoster(ev.id, posterFile);
        } catch {}
      }
      navigate(`/events/${ev.id}`);
    } catch (e) {
      alert('Erstellen fehlgeschlagen');
    }
  }

  return (
    <div className="home-page">
      <section style={{ width:'100vw', margin:0, padding:0, position:'relative', left:'-120px' }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin:'0 0 8px' }}>Neues Event</h2>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign: 'center', maxWidth:760 }}>Basisdaten und Teilnahmeoptionen festlegen</p>
      </section>
      <section style={{ width:'100%', maxWidth:900, margin:'0 auto', padding:'0 16px 40px 16px', color:'#fff' }}>
        <form onSubmit={onSubmit} style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <input required placeholder="Titel" value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} style={{ gridColumn:'1/-1' }} />
          <textarea placeholder="Beschreibung (Markdown erlaubt)" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} style={{ gridColumn:'1/-1', minHeight:140 }} />
          <div style={{ gridColumn:'1/-1', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:12 }}>
            <div style={{ fontWeight:700, marginBottom:6 }}>Vorschau</div>
            {form.description ? (
              <div style={{ color:'#cbd5e1' }} dangerouslySetInnerHTML={descriptionPreview} />
            ) : (
              <div style={{ color:'#9ca3af', fontSize:12 }}>Markdown-Unterstützung: Überschriften, Listen, Links, Bilder.</div>
            )}
          </div>
          <input placeholder="Tags (kommagetrennt)" value={form.tags} onChange={e=>setForm(f=>({...f,tags:e.target.value}))} />
          <input placeholder="Poster-URL (optional)" value={form.posterUrl} onChange={e=>setForm(f=>({...f,posterUrl:e.target.value}))} />
          <label style={{ display:'flex', flexDirection:'column' }}>
            <span style={{ fontSize:13, opacity:0.9 }}>Poster-Datei (optional)</span>
            <input type="file" accept="image/*" onChange={e=>setPosterFile(e.target.files?.[0] || null)} />
          </label>
          <label>Start <input type="datetime-local" value={form.startAt} onChange={e=>setForm(f=>({...f,startAt:e.target.value}))} required /></label>
          <label>Ende <input type="datetime-local" value={form.endAt} onChange={e=>setForm(f=>({...f,endAt:e.target.value}))} /></label>
          <select value={form.locationType} onChange={e=>setForm(f=>({...f,locationType:e.target.value}))}>
            <option value="online">Online</option>
            <option value="venue">Ort</option>
          </select>
          {form.locationType === 'venue' ? (
            <input placeholder="Adresse" value={form.venueAddress} onChange={e=>setForm(f=>({...f,venueAddress:e.target.value}))} />
          ) : (
            <input placeholder="Online-Link" value={form.onlineUrl} onChange={e=>setForm(f=>({...f,onlineUrl:e.target.value}))} />
          )}
          <select value={form.visibility} onChange={e=>setForm(f=>({...f,visibility:e.target.value}))}>
            <option value="public">öffentlich</option>
            <option value="members">community-only</option>
            <option value="private">privat</option>
          </select>
          <select value={form.registrationMode} onChange={e=>setForm(f=>({...f,registrationMode:e.target.value}))}>
            <option value="open">Anmeldung: frei</option>
            <option value="invite">Anmeldung: nur auf Einladung</option>
            <option value="ticket">Anmeldung: Ticket erforderlich (deaktiviert)</option>
          </select>
          <input placeholder="Kapazität (leer = unbegrenzt)" value={form.capacity} onChange={e=>setForm(f=>({...f,capacity:e.target.value}))} />
          <label style={{ display:'flex', alignItems:'center', gap:6 }}><input type="checkbox" checked={form.waitlistEnabled} onChange={e=>setForm(f=>({...f,waitlistEnabled:e.target.checked}))} /> Warteliste</label>
          <label style={{ display:'flex', alignItems:'center', gap:6 }}><input type="checkbox" checked={form.participantListPublic} onChange={e=>setForm(f=>({...f,participantListPublic:e.target.checked}))} /> Teilnehmerliste öffentlich</label>
          <label style={{ display:'flex', alignItems:'center', gap:6 }}><input type="checkbox" checked={form.waitlistPublic} onChange={e=>setForm(f=>({...f,waitlistPublic:e.target.checked}))} /> Warteliste öffentlich</label>
          <select value={form.eventType} onChange={e=>setForm(f=>({...f,eventType:e.target.value, typeExtras: {}}))}>
            <option value="">– Event-Typ wählen –</option>
            {EVENT_TYPES.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
            <option value="__custom__">Sonstiges …</option>
          </select>
          {form.eventType === '__custom__' && (
            <input placeholder="Eigener Event-Typ" value={form.customEventType} onChange={e=>setForm(f=>({...f,customEventType:e.target.value}))} />
          )}
          {/* Typabhängige Zusatzfelder */}
          {form.eventType && form.eventType !== '__custom__' && (EVENT_TYPE_SCHEMAS[form.eventType] || []).map(def => (
            <label key={def.key} style={{ display:'flex', flexDirection:'column' }}>
              <span style={{ fontSize:13, opacity:0.9 }}>{def.label}</span>
              <input
                type={def.type === 'url' ? 'url' : 'text'}
                placeholder={def.placeholder || ''}
                value={form.typeExtras?.[def.key] || ''}
                onChange={e=>setForm(f=>({...f, typeExtras: { ...(f.typeExtras||{}), [def.key]: e.target.value }}))}
              />
            </label>
          ))}
          {/* Owner festlegen */}
          <label style={{ gridColumn:'1/-1', display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, alignItems:'center' }}>
            <span style={{ fontSize:13, opacity:0.9 }}>Event-Owner</span>
            <select value={ownerId ?? ''} onChange={e=>setOwnerId(e.target.value ? parseInt(e.target.value,10) : null)}>
              {owners.length === 0 ? <option value="">– lädt –</option> : owners.map(u => (
                <option key={u.id} value={u.id}>{u.displayName || u.username || u.id}</option>
              ))}
            </select>
          </label>
          {/* Benutzerdefinierte Zusatzfelder (bis zu 5) */}
          <div style={{ gridColumn:'1/-1', marginTop:8 }}>
            <div style={{ fontWeight:700, marginBottom:6 }}>Zusätzliche Felder (max. 5)</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:8 }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <>
                  <input
                    key={`lbl-${i}`}
                    placeholder={`Bezeichnung ${i+1}`}
                    value={form.userExtras?.[i]?.label || ''}
                    onChange={e=>setForm(f=>{
                      const arr = (f.userExtras || []).slice();
                      arr[i] = { ...(arr[i]||{ label:'', value:'' }), label: e.target.value };
                      return { ...f, userExtras: arr };
                    })}
                  />
                  <input
                    key={`val-${i}`}
                    placeholder={`Wert ${i+1}`}
                    value={form.userExtras?.[i]?.value || ''}
                    onChange={e=>setForm(f=>{
                      const arr = (f.userExtras || []).slice();
                      arr[i] = { ...(arr[i]||{ label:'', value:'' }), value: e.target.value };
                      return { ...f, userExtras: arr };
                    })}
                  />
                </>
              ))}
            </div>
          </div>
          <textarea placeholder="Teilnahmebedingungen (optional)" value={form.termsText} onChange={e=>setForm(f=>({...f,termsText:e.target.value}))} style={{ gridColumn:'1/-1', minHeight:100 }} />
          <div style={{ gridColumn:'1/-1', display:'flex', gap:8 }}>
            <button type="submit">Erstellen</button>
            <button type="button" onClick={()=>navigate('/events')}>Abbrechen</button>
          </div>
        </form>
      </section>
    </div>
  );
}
